import { Check, ChevronsUpDown } from 'lucide-react';
import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { controlClassName, Field } from '../Field/Field.tsx';
import type { SelectOption } from '../Select/Select.tsx';
import { matchingOptions } from './combobox-search.ts';
import { ARROW_STEPS } from '../arrow-steps.ts';
import { usePlacementUnder } from '../popup-placement.ts';
import styles from './Combobox.module.css';

export type ComboboxProps = {
  label: string;
  hideLabel?: boolean;
  options: readonly SelectOption[];
  value: string;
  onChange: (value: string) => void;
  searchPlaceholder: string;
  noMatchLabel: string;
  onQueryChange?: (query: string) => void;
};

export const Combobox = ({
  label,
  hideLabel,
  options,
  value,
  onChange,
  searchPlaceholder,
  noMatchLabel,
  onQueryChange,
}: ComboboxProps) => {
  const inputId = useId();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const anchor = useRef<HTMLInputElement>(null);
  const place = usePlacementUnder(anchor, open);
  const chosen = options.find((option) => option.value === value);
  const shown = onQueryChange === undefined ? matchingOptions(options, query) : options;
  const activeOption = shown[Math.min(active, shown.length - 1)];

  const openWith = (text: string) => {
    setQuery(text);
    setActive(0);
    setOpen(true);
    onQueryChange?.(text);
  };

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const choose = (option: SelectOption) => {
    onChange(option.value);
    close();
  };

  const handleKeys = (event: KeyboardEvent<HTMLInputElement>) => {
    const step = ARROW_STEPS[event.key];
    if (step !== undefined) {
      event.preventDefault();
      if (!open) openWith('');
      else setActive((current) => (current + step + shown.length) % Math.max(shown.length, 1));
      return;
    }
    if (event.key === 'Enter' && open && activeOption !== undefined) {
      event.preventDefault();
      choose(activeOption);
      return;
    }
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      close();
    }
  };

  return (
    <div className={styles['root']}>
      <Field id={inputId} label={label} hideLabel={hideLabel}>
        <span className={styles['wrapper']}>
          <input
            ref={anchor}
            id={inputId}
            role="combobox"
            className={controlClassName(undefined, styles['input'])}
            autoComplete="off"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={
              open && activeOption !== undefined ? `${listId}-${activeOption.value}` : undefined
            }
            placeholder={open ? searchPlaceholder : (chosen?.label ?? searchPlaceholder)}
            value={open ? query : (chosen?.label ?? '')}
            onFocus={() => {
              openWith('');
            }}
            onClick={() => {
              if (!open) openWith('');
            }}
            onChange={(event) => {
              openWith(event.target.value);
            }}
            onBlur={close}
            onKeyDown={handleKeys}
          />
          <ChevronsUpDown size={16} aria-hidden="true" className={styles['chevron']} />
        </span>
      </Field>
      {open ? (
        <ul id={listId} role="listbox" aria-label={label} className={styles['list']} style={place}>
          {shown.length === 0 ? (
            <li className={styles['empty']} role="presentation">
              {noMatchLabel}
            </li>
          ) : (
            shown.map((option) => (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <li
                key={option.value}
                id={`${listId}-${option.value}`}
                role="option"
                aria-selected={option.value === value}
                data-active={option === activeOption}
                className={styles['option']}
                onPointerDown={(event) => {
                  event.preventDefault();
                }}
                onClick={() => {
                  choose(option);
                }}
              >
                <span>{option.label}</span>
                {option.value === value ? <Check size={16} aria-hidden="true" /> : null}
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
};
